import { Message, Theme } from "shared/types/chatBoard";
import { formatDateTime24h } from "./helper";

export const getInitials = (name: string): string => {
  if (!name) return "";
  const parts = name.trim().split(" ");
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

export const getThemeFromRole = (role: string): Theme => {
  const roleLower = role.toLowerCase();
  const themes: Theme[] = ["theme1", "theme2", "theme3", "theme4"];
  const roleIndex = roleLower.charCodeAt(0) % themes.length;
  return themes[roleIndex];
};

export const mapCommentToMessage = (comment: any, getInitialsFn = getInitials, getThemeFromRoleFn = getThemeFromRole): Message => {
  const createdAtRaw = comment.created_at || comment.createdAt || comment.timestamp || comment.created_at_utc;
  const createdAtIso =
    typeof createdAtRaw === "string"
      ? createdAtRaw
      : createdAtRaw
        ? new Date(createdAtRaw).toISOString()
        : "";

  const senderName = comment.user?.display_name ||
    comment.user?.name ||
    (comment.user?.first_name && comment.user?.last_name
      ? `${comment.user.first_name} ${comment.user.last_name}`
      : comment.user?.first_name || comment.user?.last_name || "Unknown");

  const senderRole = comment.user?.user_type ||
    comment.user?.role ||
    (comment.user?.email?.includes("client") ? "Client" : "User");

  const attachments = (comment.comment_assets || []).map((asset: any) => {
    const pathParts = asset.path?.split("/") || [];
    const lastPart = pathParts[pathParts.length - 1] || "";

    let extension = "";
    if (asset.mime) {
      if (asset.mime.includes("png")) extension = ".png";
      else if (asset.mime.includes("jpg") || asset.mime.includes("jpeg")) extension = ".jpg";
      else if (asset.mime.includes("gif")) extension = ".gif";
      else if (asset.mime.includes("webp")) extension = ".webp";
      else if (asset.mime.includes("pdf")) extension = ".pdf";
    } else if (asset.type === "image") {
      extension = ".png";
    } else if (asset.type === "pdf" || asset.type === "document") {
      extension = ".pdf";
    }

    const fileName = lastPart.includes(".")
      ? lastPart
      : `${lastPart || "file"}${extension}`;

    return {
      name: fileName,
      type: asset.type || (asset.mime?.startsWith("image/") ? "image" :
        asset.mime === "application/pdf" ? "pdf" : "document"),
      url: asset.path,
    };
  });

  const profileAsset = comment.user?.path || comment.user?.profile_asset || "";

  return {
    id: comment.comment_id?.toString() || comment.id?.toString() || "",
    sender: {
      name: senderName,
      role: senderRole,
      initial: getInitialsFn(senderName),
      theme: getThemeFromRoleFn(senderRole),
      profile_asset: profileAsset || undefined,
    },
    createdAt: createdAtIso || undefined,
    timestamp: formatDateTime24h(createdAtIso),
    content: comment.content || comment.comment || comment.message || "",
    attachments: attachments.length > 0 ? attachments : undefined,
    repliesCount: comment.reply_count || comment.replies_count || 0,
    lastReplyAt: comment.last_reply_at || comment.lastReplyAt,
  };
};

export const mergeUniqueMessagesById = (list: Message[]): Message[] => {
  const indexById = new Map<string, number>();
  const result: Message[] = [];

  list.forEach((msg) => {
    const id = (msg?.id ?? "").toString();
    if (!id) return;

    const existingIndex = indexById.get(id);
    if (existingIndex !== undefined) {
      result[existingIndex] = msg;
      return;
    }

    indexById.set(id, result.length);
    result.push(msg);
  });

  return result;
};

export const sortMessagesChronologically = (list: Message[]): Message[] => {
  const toMs = (msg: Message) => {
    const raw = msg.createdAt;
    const ms = raw ? Date.parse(raw) : NaN;
    return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY;
  };

  return [...list].sort((a, b) => toMs(a) - toMs(b));
};
export const mergeAndSortMessages = (list: Message[]): Message[] => {
  return sortMessagesChronologically(mergeUniqueMessagesById(list));
};

export const sortRepliesChronologically = (replies: any[]): any[] => {
  return [...replies].sort((a, b) => {
    const dateA = new Date(a.created_at || a.createdAt || a.timestamp || 0).getTime();
    const dateB = new Date(b.created_at || b.createdAt || b.timestamp || 0).getTime();
    return dateA - dateB;
  });
};

export const mapReplyToMessage = (reply: any, getInitialsFn = getInitials, getThemeFromRoleFn = getThemeFromRole): Message => {
  const createdAtRaw = reply.created_at || reply.createdAt || reply.timestamp || reply.created_at_utc;
  const createdAtIso =
    typeof createdAtRaw === "string"
      ? createdAtRaw
      : createdAtRaw
        ? new Date(createdAtRaw).toISOString()
        : "";

  const senderName = reply.user?.display_name ||
    reply.user?.name ||
    (reply.user?.first_name && reply.user?.last_name
      ? `${reply.user.first_name} ${reply.user.last_name}`
      : reply.user?.first_name || reply.user?.last_name || "Unknown");

  const senderRole = reply.user?.user_type ||
    reply.user?.role ||
    (reply.user?.email?.includes("client") ? "Client" : "User");

  const attachments = (reply.reply_assets || reply.assets || []).map((asset: any) => {
    const pathParts = asset.path?.split("/") || [];
    const lastPart = pathParts[pathParts.length - 1] || "";

    let extension = "";
    if (asset.mime) {
      if (asset.mime.includes("png")) extension = ".png";
      else if (asset.mime.includes("jpg") || asset.mime.includes("jpeg")) extension = ".jpg";
      else if (asset.mime.includes("gif")) extension = ".gif";
      else if (asset.mime.includes("webp")) extension = ".webp";
      else if (asset.mime.includes("pdf")) extension = ".pdf";
    } else if (asset.type === "image") {
      extension = ".png";
    } else if (asset.type === "pdf" || asset.type === "document") {
      extension = ".pdf";
    }

    const fileName = lastPart.includes(".")
      ? lastPart
      : `${lastPart || "file"}${extension}`;

    return {
      name: fileName,
      type: asset.type || (asset.mime?.startsWith("image/") ? "image" :
        asset.mime === "application/pdf" ? "pdf" : "document"),
      url: asset.path,
    };
  });

  const profileAsset = reply.user?.path || reply.user?.profile_asset || "";

  return {
    id: reply.reply_id?.toString() || reply.id?.toString() || "",
    sender: {
      name: senderName,
      role: senderRole,
      initial: getInitialsFn(senderName),
      theme: getThemeFromRoleFn(senderRole),
      profile_asset: profileAsset || undefined,
    },
    createdAt: createdAtIso || undefined,
    timestamp: formatDateTime24h(createdAtIso),
    content: reply.content || reply.reply || reply.message || "",
    attachments: attachments.length > 0 ? attachments : undefined,
    repliesCount: 0,
    lastReplyAt: undefined,
  };
};

export const stripHtmlTags = (html: string): string => {
  if (!html) return "";
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

